import React from 'react';

import { useConfluenceAnalyticsEvents } from '@confluence/analytics/entry-points/useConfluenceAnalyticsEvents.ts';
// eslint-disable-next-line no-restricted-imports -- deprecated - use RouteState instead
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { isEmbeddedRovoSystemSpaceAccessAllowed } from '@confluence/route-manager/entry-points/isEmbeddedRovoSystemSpaceAccessAllowed.ts';
import { isRovoSystemSpaceKey } from '@confluence/route-manager/entry-points/isRovoSystemSpaceKey.ts';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';
import { NotFound } from '@confluence/not-found/entry-points/NotFound.ts';
import { fg } from '@confluence/feature-gating';

type WithRovoSystemSpaceNotFoundProps = {
	spaceKey: string;
};

export const withRovoSystemSpaceNotFound = <T extends WithRovoSystemSpaceNotFoundProps>(
	Component: React.ComponentType<T>,
): React.ComponentType<T> => {
	return (props: T) => {
		const { location, match } = React.useContext(RoutesContext);
		const isEditorPage = useIsEditorPage();
		const isEmbeddedRovoSystemSpaceAllowed = isEmbeddedRovoSystemSpaceAccessAllowed({
			isEditorPage,
			location,
		});
		const { createEvent } = useConfluenceAnalyticsEvents();

		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		const isBlocked =
			isRovoSystemSpaceKey(props.spaceKey) &&
			!isEmbeddedRovoSystemSpaceAllowed &&
			fg('aifc_create_enabled');

		const routeName = match?.name;
		const contentId = match?.params?.contentId;
		const hasDraftShareId = Boolean(match?.query?.draftShareId);

		// Smart-create drafts live in a Rovo system space until they are published, so a shared link
		// to one bounces through the view route into the editor and lands here. The <NotFound /> we
		// render below is indistinguishable from every other 404 in analytics, and the edit-page
		// experience is stopped as a taskSuccess (see `stopOnNoPermissionOrNotFound` in
		// @confluence/experience-tracker), so without this event the block leaves no trace at all.
		React.useEffect(() => {
			if (!isBlocked) {
				return;
			}

			createEvent({
				type: 'sendOperationalEvent',
				data: {
					source: 'withRovoSystemSpaceNotFound',
					action: 'blocked',
					actionSubject: 'rovoSystemSpaceContent',
					actionSubjectId: 'notFound',
					attributes: {
						routeName,
						contentId,
						hasDraftShareId,
						isEditorPage,
					},
				},
			}).fire();
		}, [isBlocked, createEvent, routeName, contentId, hasDraftShareId, isEditorPage]);

		if (isBlocked) {
			return <NotFound />;
		}
		return <Component {...props} />;
	};
};
